import { formatToHumanReadable } from "@/lib/format-data";

import type {
  PreparedSettingsPatchPayload,
  PreparedSettingsPatchReviewUnit,
  SettingsPatchReviewModel,
  SettingsPatchReviewSection,
  SettingsPatchSelectableChange,
} from "./types";

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function formatForUnit(
  unit: PreparedSettingsPatchReviewUnit,
  diff: PreparedSettingsPatchPayload["diff"],
): string | undefined {
  if (unit.format) return unit.format;
  const match = diff.find((entry) => {
    const fieldId = entry.fieldId ?? entry.field_id;
    const operationId =
      entry.operationId ??
      entry.operation_id ??
      (fieldId ? `field:${fieldId}` : undefined);
    const recordId = entry.recordId ?? entry.record_id;
    return (
      fieldId === unit.fieldId &&
      operationId === unit.operationId &&
      recordId === unit.recordId
    );
  });
  return readString(match?.format);
}

function unitsWithFormats(
  units: readonly PreparedSettingsPatchReviewUnit[],
  diff: PreparedSettingsPatchPayload["diff"],
): PreparedSettingsPatchReviewUnit[] {
  return units.map((unit) => ({
    ...unit,
    format: formatForUnit(unit, diff),
  }));
}

function presentPolicyUnit(
  unit: PreparedSettingsPatchReviewUnit,
): PreparedSettingsPatchReviewUnit {
  if (
    unit.targetId !== "tenant.approvalPolicies" ||
    unit.fieldId !== "fieldIds"
  ) {
    return unit;
  }
  const displayFields = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.length === 0
        ? "All supported service fields"
        : value.map((field) => formatToHumanReadable(String(field))).join(", ")
      : value;
  return {
    ...unit,
    from: displayFields(unit.from),
    to: displayFields(unit.to),
    format: "plain",
  };
}

function buildAtomicClosures(
  units: readonly PreparedSettingsPatchReviewUnit[],
): SettingsPatchSelectableChange[] {
  const unitsByAtomicId = new Map<string, PreparedSettingsPatchReviewUnit[]>();
  for (const unit of units) {
    const closure = unitsByAtomicId.get(unit.atomicId) ?? [];
    closure.push(unit);
    unitsByAtomicId.set(unit.atomicId, closure);
  }
  return Array.from(unitsByAtomicId, ([id, closureUnits]) => {
    // Context units are displayed but never selected: the server rejects them, so sending their
    // ids back would fail an otherwise valid apply.
    const approvable = closureUnits.filter((unit) => unit.selectable !== false);
    return {
      id,
      unitIds: approvable.map((unit) => unit.id),
      units: closureUnits,
      compound: closureUnits.length > 1,
      selectable: approvable.length > 0,
      atomicReason: closureUnits.find((unit) => unit.atomicReason)
        ?.atomicReason,
    };
  });
}

function flatSection(
  changes: SettingsPatchSelectableChange[],
): SettingsPatchReviewSection {
  return {
    id: "proposed-changes",
    heading: "Proposed changes",
    changes,
    unitCount: changes.reduce(
      (count, change) => count + change.units.length,
      0,
    ),
  };
}

/**
 * Project the engine review contract without inferring intent or selection.
 * Intent headings come only from persisted review.intentGroups. Atomic IDs are
 * used solely to preserve server-enforced apply-together selection.
 */
export function projectPreparedSettingsPatch(
  patch: PreparedSettingsPatchPayload,
): SettingsPatchReviewModel {
  const units = unitsWithFormats(
    patch.review?.units ?? [],
    patch.diff ?? [],
  ).map(presentPolicyUnit);
  const changes = buildAtomicClosures(units);
  const changeByUnitId = new Map<string, SettingsPatchSelectableChange>();
  for (const change of changes) {
    // Map every unit, context included: section placement and rendering are driven by this map,
    // while change.unitIds stays selection-only.
    for (const unit of change.units) changeByUnitId.set(unit.id, change);
  }

  const intentGroups = patch.review?.intentGroups ?? [];
  const submittedUnitIds = intentGroups.flatMap((group) => group.unitIds);
  const coveredUnitIds = new Set(submittedUnitIds);
  const knownUnitIds = new Set(units.map((unit) => unit.id));
  const sectionByAtomicId = new Map<string, string>();
  let atomicClosuresStayInOneSection = true;
  for (const group of intentGroups) {
    for (const unitId of group.unitIds) {
      const atomicId = changeByUnitId.get(unitId)?.id;
      if (!atomicId) continue;
      const priorSection = sectionByAtomicId.get(atomicId);
      if (priorSection && priorSection !== group.id) {
        atomicClosuresStayInOneSection = false;
      }
      sectionByAtomicId.set(atomicId, group.id);
    }
  }
  const hasExactIntentCoverage =
    intentGroups.length > 0 &&
    submittedUnitIds.length === units.length &&
    coveredUnitIds.size === units.length &&
    submittedUnitIds.every((unitId) => knownUnitIds.has(unitId)) &&
    units.every((unit) => coveredUnitIds.has(unit.id)) &&
    atomicClosuresStayInOneSection;

  const sections: SettingsPatchReviewSection[] = hasExactIntentCoverage
    ? intentGroups.map((group) => {
        const seenChanges = new Set<string>();
        const groupChanges: SettingsPatchSelectableChange[] = [];
        for (const unitId of group.unitIds) {
          const change = changeByUnitId.get(unitId);
          if (change && !seenChanges.has(change.id)) {
            seenChanges.add(change.id);
            groupChanges.push(change);
          }
        }
        return {
          id: group.id,
          heading: group.requestedIntent,
          requestedIntent: group.requestedIntent,
          changes: groupChanges,
          unitCount: group.unitIds.length,
        };
      })
    : [flatSection(changes)];

  return {
    patchId: patch.patchId,
    targetId: patch.targetId ?? patch.target_id,
    presentationKind: hasExactIntentCoverage
      ? "intent-groups"
      : "flat-fallback",
    sections,
    changes,
    unitCount: units.length,
    permissions: patch.permissions,
    risk: patch.risk,
    raw: {
      diff: patch.diff,
      operations: patch.operations,
      review: patch.review,
    },
  };
}

export function selectedUnitIdsForChanges(
  changes: readonly SettingsPatchSelectableChange[],
  selectedChangeIds: ReadonlySet<string>,
): string[] {
  return changes.flatMap((change) =>
    change.selectable && selectedChangeIds.has(change.id) ? change.unitIds : [],
  );
}
